import React from "react";
import PropTypes from "prop-types";
import { Empty, Icon, Modal, Switch, Tag } from "antd";
import AddAssociateBankAccountForm from "forms/add-associate-bank-account-form";
import { BankAccountRecord } from "records/company-record";
import { STATE_STATUS } from "constants/state-status";
import KeyBankAccountSwitcher from "../key-bank-account-switcher";
import CardBlock from "./components/card-block";
import { BANK_ACCOUNT_TYPES } from "constants";

/**
 * Bank Accounts — the Credit Checker's own compact table/grid, now with the same
 * ACTIONS that Case Details ▸ Bank Accounts offers (they were missing here):
 *
 *   - Add Account                            (header)
 *   - Edit Account                           (per row)
 *   - Change Key Bank Account                (header, KeyBankAccountSwitcher)
 *   - Select Bank Account Applicable To Case (header)
 *
 * The presentation stays this tab's own — we deliberately do NOT reuse
 * BankAccountAddList, whose antd card layout does not match the Credit Checker
 * surface. Only the behaviour is shared: same form, same switcher, same
 * `updateCompanyBankAccounts` / `addCaseBankAccounts` actions, so an edit here is
 * the same write Case Details performs.
 * The account with `is_key_account === true` gets a green "Key Account" chip
 * on its row.
 *
 * Two view modes:
 *   - Row (default) — one horizontal row per account with all fields on a line
 *   - Grid          — one tile per account with fields in a 2-column grid
 *
 * Source: company.bank_accounts (loaded by the case-detail saga alongside the
 * rest of the company record — nothing extra to fetch here).
 */

// BANK_ACCOUNT_TYPES is an Immutable Map<label, id>. Immutable's `.filter`
// signature is `(value, key) => bool`, so filter by VALUE === typeId to find
// the row, then return its KEY (the label). Previously this was flipped and
// so the reverse-lookup always missed → raw enum ids leaked into the UI.
const ACCOUNT_TYPE_LABEL = typeId => {
    if (typeId === null || typeId === undefined || typeId === "") return "—";
    const numericId = Number(typeId);
    const label = Array.from(
        BANK_ACCOUNT_TYPES.filter(value => value === numericId).keys()
    )[0];
    return label ? String(label).toUpperCase() : String(typeId);
};

const toList = bankAccounts => {
    if (!bankAccounts) return [];
    if (typeof bankAccounts.toList === "function") {
        return bankAccounts.toList().toArray();
    }
    if (Array.isArray(bankAccounts)) return bankAccounts;
    return [];
};

const readField = (row, key) => {
    if (row === null || row === undefined) return undefined;
    if (row.get) return row.get(key);
    return row[key];
};

const readBranch = (row, key) => {
    const branch = readField(row, "bank_branch");
    if (!branch) return undefined;
    if (branch.get) return branch.get(key);
    return branch[key];
};

// One horizontal row per account — the default, more compact view.
const AccountRow = ({ row, isKey, canEdit, onEdit }) => {
    const acctType = readField(row, "account_type");
    const holder = readField(row, "account_holder_name");
    const number = readField(row, "account_number");
    const limit = readField(row, "account_limit");
    const ifsc = readBranch(row, "ifsc_code");
    const bank = readBranch(row, "bank_name");
    const branch = readBranch(row, "name");
    return (
        <tr className={isKey ? "cc-bank-row cc-bank-row--key" : "cc-bank-row"}>
            <td className={"cc-bank-row__bank"}>
                <span>{bank || "Bank"}</span>
                {isKey ? (
                    <Tag color={"green"} style={{ marginLeft: 6 }}>
                        Key
                    </Tag>
                ) : null}
            </td>
            <td>{ACCOUNT_TYPE_LABEL(acctType)}</td>
            <td>{holder || "—"}</td>
            <td className={"cc-kyc-mono"}>{number || "—"}</td>
            <td>{limit || limit === 0 ? String(limit) : "—"}</td>
            <td className={"cc-kyc-mono"}>{ifsc || "—"}</td>
            <td>{branch || "—"}</td>
            {canEdit ? (
                <td>
                    <a onClick={onEdit}>
                        Edit <Icon type={"edit"} />
                    </a>
                </td>
            ) : null}
        </tr>
    );
};
AccountRow.propTypes = {
    row: PropTypes.object,
    isKey: PropTypes.bool,
    canEdit: PropTypes.bool,
    onEdit: PropTypes.func
};

// One tile per account — the wider "card" view.
const AccountTile = ({ row, isKey, canEdit, onEdit }) => {
    const acctType = readField(row, "account_type");
    const holder = readField(row, "account_holder_name");
    const number = readField(row, "account_number");
    const limit = readField(row, "account_limit");
    const ifsc = readBranch(row, "ifsc_code");
    const bank = readBranch(row, "bank_name");
    const branch = readBranch(row, "name");
    return (
        <div className={"cc-bank-acct" + (isKey ? " cc-bank-acct--key" : "")}>
            <div className={"cc-bank-acct__header"}>
                <span className={"cc-bank-acct__bank"}>{bank || "Bank"}</span>
                {canEdit ? (
                    <a
                        onClick={onEdit}
                        style={{ marginLeft: "auto", marginRight: 8 }}
                    >
                        Edit <Icon type={"edit"} />
                    </a>
                ) : null}
                {isKey ? <Tag color={"green"}>Key Account</Tag> : null}
            </div>
            <div className={"cc-bank-acct__grid"}>
                <div className={"cc-bank-acct__cell"}>
                    <div className={"cc-bank-acct__label"}>A/C Type</div>
                    <div className={"cc-bank-acct__value"}>
                        {ACCOUNT_TYPE_LABEL(acctType)}
                    </div>
                </div>
                <div className={"cc-bank-acct__cell"}>
                    <div className={"cc-bank-acct__label"}>
                        A/C Holder Name
                    </div>
                    <div className={"cc-bank-acct__value"}>
                        {holder || "—"}
                    </div>
                </div>
                <div className={"cc-bank-acct__cell"}>
                    <div className={"cc-bank-acct__label"}>A/C Number</div>
                    <div className={"cc-bank-acct__value"}>
                        {number || "—"}
                    </div>
                </div>
                <div className={"cc-bank-acct__cell"}>
                    <div className={"cc-bank-acct__label"}>A/C Limit</div>
                    <div className={"cc-bank-acct__value"}>
                        {limit || limit === 0 ? String(limit) : "—"}
                    </div>
                </div>
                <div className={"cc-bank-acct__cell"}>
                    <div className={"cc-bank-acct__label"}>IFSC</div>
                    <div className={"cc-bank-acct__value"}>{ifsc || "—"}</div>
                </div>
                <div className={"cc-bank-acct__cell"}>
                    <div className={"cc-bank-acct__label"}>Branch</div>
                    <div className={"cc-bank-acct__value"}>
                        {branch || "—"}
                    </div>
                </div>
            </div>
        </div>
    );
};
AccountTile.propTypes = {
    row: PropTypes.object,
    isKey: PropTypes.bool,
    canEdit: PropTypes.bool,
    onEdit: PropTypes.func
};

class BankAccountCard extends React.Component {
    state = {
        view: "row",
        adding: false,
        editing: false,
        switching: false,
        applicableOpen: false,
        // Local working copy of the case↔account applicability map, seeded from
        // redux and committed on OK.
        caseBankAccounts: null
    };

    formRef = React.createRef();
    switcherRef = React.createRef();
    // The account currently being edited (null while adding).
    bankAccount = null;

    componentDidMount() {
        if (this.props.fetchCaseBankAccounts && this.props.caseId) {
            this.props.fetchCaseBankAccounts(this.props.caseId);
        }
    }

    componentDidUpdate(prevProps) {
        if (
            this.props.caseBankAccountsStatus === STATE_STATUS.FETCHED &&
            (!this.state.caseBankAccounts ||
                prevProps.caseBankAccounts !== this.props.caseBankAccounts)
        ) {
            this.setState({
                caseBankAccounts: this.props.caseBankAccounts || []
            });
        }
    }

    setView = view => this.setState({ view });

    accounts = () =>
        (this.props.company &&
            this.props.company.get &&
            this.props.company.get("bank_accounts")) ||
        null;

    canEdit = () =>
        Boolean(
            this.props.isUserAllowedToEdit &&
                !this.props.recreditViewOnlyPermission
        );

    toggleAdd = () => this.setState(p => ({ adding: !p.adding }));

    toggleEdit = () => this.setState(p => ({ editing: !p.editing }));

    toggleSwitch = () => this.setState(p => ({ switching: !p.switching }));

    toggleApplicable = () =>
        this.setState(p => ({ applicableOpen: !p.applicableOpen }));

    startEdit = accountId => {
        const accounts = this.accounts();
        this.bankAccount = accounts
            ? accounts.find(_ => _.id === accountId)
            : null;
        this.toggleEdit();
    };

    // Add / Edit both write through updateCompanyBankAccounts — the same action
    // Case Details uses, so this is the identical persistence path.
    saveBankAccount = actionType => {
        this.formRef.current.validateFields((err, values) => {
            if (err) {
                return;
            }
            const accounts = this.accounts();
            values.associate =
                values.associate === "company" ? null : values.associate;
            this.props.updateCompanyBankAccounts(
                Number(this.props.companyId),
                [
                    new BankAccountRecord({
                        id: this.bankAccount ? this.bankAccount.id : null,
                        company: Number(this.props.companyId),
                        // The first account on a company is the key account by
                        // definition; otherwise preserve whatever it already was.
                        is_key_account:
                            !accounts || !accounts.size
                                ? true
                                : this.bankAccount
                                    ? this.bankAccount.is_key_account
                                    : false,
                        ...values
                    })
                ],
                Number(this.props.caseId)
            );
            this.bankAccount = null;
            if (actionType === "add") {
                this.toggleAdd();
                return;
            }
            this.toggleEdit();
        });
    };

    // Key-account switch: demote the old, promote the new, in one write.
    switchKeyAccount = () => {
        const switcher = this.switcherRef.current;
        const s = switcher && switcher.state;
        if (!s || s.newKeyAccountId === s.oldKeyAccountId) {
            this.toggleSwitch();
            return;
        }
        const accounts = this.accounts();
        const next = accounts
            .find(_ => _.id === s.newKeyAccountId)
            .set("is_key_account", true);
        const prev = accounts.find(_ => _.id === s.oldKeyAccountId);
        this.toggleSwitch();
        this.props.updateCompanyBankAccounts(
            Number(this.props.companyId),
            prev
                ? [prev.set("is_key_account", false), next]
                : [next],
            Number(this.props.caseId)
        );
    };

    isApplicable = account => {
        const list = this.state.caseBankAccounts;
        if (!list || !list.length) {
            return false;
        }
        const match = list.find(
            row => String(row.company_bank_account) === String(account.id)
        );
        return match ? Boolean(match.is_applicable) : false;
    };

    // Note: writes `is_applicable` (not `type`) and checks `idx === -1`
    // explicitly — index 0 is falsy, so a truthiness test would append a
    // duplicate row for the first account.
    onToggleApplicable = (accountId, checked) =>
        this.setState(prev => {
            const list = prev.caseBankAccounts
                ? [...prev.caseBankAccounts]
                : [];
            const idx = list.findIndex(
                row => String(row.company_bank_account) === String(accountId)
            );
            if (idx === -1) {
                list.push({
                    company_bank_account: accountId,
                    is_applicable: checked
                });
            } else {
                list[idx] = { ...list[idx], is_applicable: checked };
            }
            return { caseBankAccounts: list };
        });

    saveApplicable = () => {
        const list = this.state.caseBankAccounts || [];
        this.setState({ applicableOpen: false }, () => {
            if (this.props.addCaseBankAccounts) {
                this.props.addCaseBankAccounts(
                    Number(this.props.caseId),
                    list.map(row => ({
                        id: row.company_bank_account,
                        is_applicable: row.is_applicable
                    }))
                );
            }
        });
    };

    renderActions() {
        if (!this.canEdit()) {
            return this.renderToggle();
        }
        return (
            <span className={"cc-bank-actions"}>
                <a onClick={this.toggleSwitch}>
                    <Icon type={"swap"} /> Change Key Account
                </a>
                <a onClick={this.toggleAdd}>
                    <Icon type={"plus"} /> Add Account
                </a>
                <a onClick={this.toggleApplicable}>
                    <Icon type={"check-square"} /> Applicable To Case
                </a>
                {this.renderToggle()}
            </span>
        );
    }

    renderModals() {
        const accounts = this.accounts();
        const associates =
            (this.props.company &&
                this.props.company.get &&
                this.props.company.get("associates")) ||
            null;
        // AddAssociateBankAccountForm expects an Immutable List of associates.
        const associateList =
            associates && associates.toList ? associates.toList() : [];
        return (
            <React.Fragment>
                <Modal
                    visible={this.state.adding}
                    width={800}
                    maskClosable
                    onCancel={this.toggleAdd}
                    onOk={() => this.saveBankAccount("add")}
                    okText={"Submit"}
                    title={"Add Bank Account"}
                    destroyOnClose
                >
                    <AddAssociateBankAccountForm
                        ref={this.formRef}
                        associates={associateList}
                    />
                </Modal>
                <Modal
                    visible={this.state.editing}
                    width={800}
                    maskClosable
                    onCancel={this.toggleEdit}
                    onOk={() => this.saveBankAccount("edit")}
                    okText={"Submit"}
                    title={"Edit Bank Account"}
                    destroyOnClose
                >
                    <AddAssociateBankAccountForm
                        ref={this.formRef}
                        values={this.bankAccount}
                        associates={associateList}
                    />
                </Modal>
                <Modal
                    visible={this.state.switching}
                    width={600}
                    maskClosable
                    onCancel={this.toggleSwitch}
                    onOk={this.switchKeyAccount}
                    okText={"Switch"}
                    title={"Change Key Bank Account"}
                    destroyOnClose
                >
                    <KeyBankAccountSwitcher
                        bankAccounts={accounts}
                        ref={this.switcherRef}
                    />
                </Modal>
                <Modal
                    visible={this.state.applicableOpen}
                    title={"Select Bank Account Applicable To Case"}
                    onOk={this.saveApplicable}
                    onCancel={this.toggleApplicable}
                    closable
                    destroyOnClose
                >
                    <div className={"w-100 p-5"}>
                        {accounts
                            ? accounts
                                .map((account, k) => (
                                    <div
                                        key={k}
                                        className={
                                            "mt-5 p-5 mb-5 d-flex justify-content-space-between"
                                        }
                                    >
                                        <div className={"d-flex flex-column"}>
                                            <span
                                                style={{ fontWeight: "600" }}
                                            >
                                                {account.account_holder_name ||
                                                    ""}
                                            </span>
                                            <span>
                                                {account.account_number || ""}
                                            </span>
                                            <span>
                                                {(account.bank_branch &&
                                                    account.bank_branch.name) ||
                                                    ""}
                                            </span>
                                        </div>
                                        <Switch
                                            checked={this.isApplicable(account)}
                                            onChange={checked =>
                                                this.onToggleApplicable(
                                                    account.id,
                                                    checked
                                                )
                                            }
                                        />
                                    </div>
                                ))
                                .toList()
                                .toArray()
                            : null}
                    </div>
                </Modal>
            </React.Fragment>
        );
    }

    renderToggle() {
        const options = [
            { key: "row", label: "Row", icon: "bars" },
            { key: "grid", label: "Grid", icon: "appstore" }
        ];
        return (
            <div className={"cc-unit-toggle"} role={"group"}>
                {options.map(opt => (
                    <button
                        key={opt.key}
                        type={"button"}
                        className={
                            "cc-unit-toggle__opt" +
                            (this.state.view === opt.key ? " is-active" : "")
                        }
                        aria-pressed={this.state.view === opt.key}
                        onClick={() => this.setView(opt.key)}
                    >
                        <Icon type={opt.icon} />
                        <span style={{ marginLeft: 4 }}>{opt.label}</span>
                    </button>
                ))}
            </div>
        );
    }

    render() {
        const { company } = this.props;
        const rows = toList(company && company.get && company.get("bank_accounts"));
        const canEdit = this.canEdit();
        if (!rows.length) {
            return (
                <CardBlock
                    icon={"bank"}
                    title={"Bank Accounts"}
                    subtitle={"Company operating accounts on record"}
                    action={canEdit ? this.renderActions() : null}
                >
                    {this.renderModals()}
                    <Empty description={"No bank accounts recorded for this company"} />
                </CardBlock>
            );
        }
        return (
            <CardBlock
                icon={"bank"}
                title={"Bank Accounts"}
                subtitle={"Company operating accounts on record"}
                action={this.renderActions()}
            >
                {this.renderModals()}
                {this.state.view === "row" ? (
                    <div className={"cc-bank-table-wrap"}>
                        <table className={"cc-bank-table"}>
                            <thead>
                                <tr>
                                    <th>Bank</th>
                                    <th>A/C Type</th>
                                    <th>A/C Holder</th>
                                    <th>A/C Number</th>
                                    <th>Limit</th>
                                    <th>IFSC</th>
                                    <th>Branch</th>
                                    {canEdit ? <th /> : null}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, idx) => {
                                    const isKey = Boolean(
                                        readField(row, "is_key_account")
                                    );
                                    const id = readField(row, "id");
                                    return (
                                        <AccountRow
                                            key={id || idx}
                                            row={row}
                                            isKey={isKey}
                                            canEdit={canEdit}
                                            onEdit={() => this.startEdit(id)}
                                        />
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className={"cc-bank-accts"}>
                        {rows.map((row, idx) => {
                            const isKey = Boolean(
                                readField(row, "is_key_account")
                            );
                            const id = readField(row, "id");
                            return (
                                <AccountTile
                                    key={id || idx}
                                    row={row}
                                    isKey={isKey}
                                    canEdit={canEdit}
                                    onEdit={() => this.startEdit(id)}
                                />
                            );
                        })}
                    </div>
                )}
            </CardBlock>
        );
    }
}

BankAccountCard.propTypes = {
    // Immutable company record (from redux). Reads bank_accounts + associates.
    company: PropTypes.object,
    caseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // Case↔account applicability map ("Applicable To Case").
    caseBankAccounts: PropTypes.array,
    caseBankAccountsStatus: PropTypes.string,
    fetchCaseBankAccounts: PropTypes.func,
    addCaseBankAccounts: PropTypes.func,
    // Same write path Case Details uses for add / edit / key-account switch.
    updateCompanyBankAccounts: PropTypes.func,
    isUserAllowedToEdit: PropTypes.bool,
    recreditViewOnlyPermission: PropTypes.bool
};

export default BankAccountCard;
